import React from 'react';
import { Link } from 'react-router-dom';
import { Award, Heart, Truck, ArrowRight, ShieldCheck, CheckCircle, Sparkles } from 'lucide-react';

const values = [
  { icon: Award, title: 'Certified Health', desc: 'Every pet undergoes professional deworming, vaccination, and microchipping prior to adoption.' },
  { icon: Heart, title: 'Welfare First', desc: 'We prioritize the safety, happiness, and adaptation of animals above all, securing loving forever homes.' },
  { icon: ShieldCheck, title: 'Verified Clearance', desc: 'Pedigree record authentication, health tracking, and vet certification charts provided.' },
  { icon: Truck, title: 'Climate Transit', desc: 'Transported carefully in temperature-regulated, customized pet-safe vans to minimize stress.' },
];

const promises = [
  'Vet-inspected & certified puppies & kittens',
  'Deworming & complete vaccination cards',
  'Organic, veterinarian-approved pet foods',
  'Comfortable, memory foam orthopedic bedding',
  'Safe, climate-controlled Nationwide transit',
  '24/7 post-adoption parenting support line',
];

export default function AboutPage() {
  return (
    <div className="min-h-screen bg-[#FAF8F5] pb-12">
      {/* Immersive Editorial Hero */}
      <div className="relative bg-primary text-white overflow-hidden py-24 md:py-32 rounded-b-[40px] shadow-premium">
        <div className="absolute inset-0 bg-gradient-to-br from-[#1E1510] via-primary-500 to-[#150E0A] opacity-95" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 text-center space-y-5 z-10">
          <span className="inline-flex items-center gap-1.5 bg-accent/20 text-accent text-[9px] font-bold tracking-[0.25em] uppercase px-3 py-1 rounded-full">
            <Sparkles size={11} /> Since 2018
          </span>
          <h1 className="text-4xl md:text-6xl font-serif font-extrabold leading-tight">About Royal Pet Store</h1>
          <p className="text-gray-300 text-sm md:text-base max-w-2xl mx-auto leading-relaxed font-medium">
            Royal Pet Store is Pakistan’s premium boutique pet center, dedicated to connecting loving families with vet-certified healthy companions, organic nutrition, and handcrafted accessories.
          </p>
        </div>
      </div>

      {/* Corporate Story */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <div className="lg:col-span-7 space-y-6">
            <span className="text-accent text-[10px] font-extrabold uppercase tracking-[0.25em]">Our Legacy</span>
            <h2 className="text-3xl md:text-4.5xl font-serif font-extrabold text-primary leading-tight">
              Setting the Gold Standard for Pet Welfare & Care
            </h2>
            <div className="w-12 h-0.5 bg-accent/30" />
            <div className="text-gray-600 text-sm leading-relaxed space-y-4 font-medium">
              <p>
                Royal Pet Store was established to revolutionize pet care and adoptions. We wanted to build a secure, transparent, and certified path for bringing new family members home.
              </p>
              <p>
                Our team works exclusively alongside professional breeders, certified veterinarians, and behavioral experts. This ensures all companions are well-socialized, healthy, and vaccinated before meeting their new parents.
              </p>
              <p>
                Based in Rawalpindi, we manage Pakistan’s first climate-regulated pet shipping fleet and feature round-the-clock support to help families navigate their pet parenting journey.
              </p>
            </div>
          </div>
          
          <div className="lg:col-span-5 bg-primary rounded-[32px] p-8 md:p-10 text-white shadow-premium relative overflow-hidden">
            <div className="absolute top-0 right-0 w-24 h-24 bg-accent/10 rounded-full blur-2xl" />
            <div className="grid grid-cols-2 gap-8 relative z-10">
              {[
                { stat: '10k+', label: 'Happy Homes' },
                { stat: '500+', label: 'Organic Feeds' },
                { stat: '100%', label: 'Vet Certified' },
                { stat: '24/7', label: 'Parent Helpline' },
              ].map((item, idx) => (
                <div key={idx} className="space-y-1">
                  <div className="text-3.5xl font-serif font-black text-accent">{item.stat}</div>
                  <div className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">{item.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Brand Promises Grid */}
      <div className="bg-white py-20 border-y border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12 space-y-1">
            <span className="text-accent text-[10px] font-extrabold uppercase tracking-[0.25em]">Our Standards</span>
            <h2 className="text-3xl font-serif font-extrabold text-primary">The Royal Pet Store Promise</h2>
            <div className="w-10 h-0.5 bg-accent/40 mx-auto mt-4" />
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-5">
            {promises.map((p, idx) => (
              <div key={idx} className="flex items-center gap-4 bg-[#FCFAF7] rounded-2xl px-6 py-5 border border-gray-150/50 shadow-sm transition-all duration-300 hover:shadow-premium">
                <div className="w-8 h-8 rounded-full bg-green-500/10 text-green-700 flex items-center justify-center flex-shrink-0">
                  <CheckCircle size={16} />
                </div>
                <span className="text-xs text-gray-750 font-bold uppercase tracking-wide">{p}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Core Values Cards */}
      <div className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-14 space-y-1">
            <span className="text-accent text-[10px] font-extrabold uppercase tracking-[0.25em]">What We Stand For</span>
            <h2 className="text-3xl font-serif font-extrabold text-primary">Our Core Values</h2>
            <div className="w-10 h-0.5 bg-accent/40 mx-auto mt-4" />
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((v, idx) => (
              <div key={idx} className="bg-white rounded-3xl p-6 border border-gray-100 text-center hover:shadow-premium transition-all duration-300">
                <div className="w-12 h-12 bg-accent/10 rounded-2xl flex items-center justify-center mx-auto mb-5 text-accent shadow-sm">
                  <v.icon size={22} />
                </div>
                <h3 className="font-extrabold text-primary text-sm uppercase tracking-wider mb-2">{v.title}</h3>
                <p className="text-gray-500 text-xs leading-relaxed font-semibold">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Call To Action */}
      <div className="bg-primary text-white py-16 px-4 text-center rounded-[32px] mx-4 md:mx-8 shadow-premium relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-tr from-[#1E1510] via-primary-500/90 to-[#150E0A]" />
        <div className="relative z-10 space-y-6 max-w-lg mx-auto">
          <h2 className="text-3xl font-serif font-extrabold text-accent">Bring a Bundle of Joy Home</h2>
          <p className="text-gray-300 text-xs md:text-sm font-semibold max-w-sm mx-auto leading-relaxed">
            Connect with our veterinary experts to view available puppies, kittens, and pedigree birds.
          </p>
          <div className="flex flex-col sm:flex-row gap-3.5 justify-center pt-2">
            <Link to="/new-in" className="inline-flex items-center justify-center gap-2 bg-accent hover:bg-accent/80 text-white px-8 py-3.5 text-xs font-bold uppercase tracking-widest transition-all rounded-xl shadow-md">
              Shop Arrivals <ArrowRight size={14} />
            </Link>
            <Link to="/contact" className="inline-flex items-center justify-center gap-2 border-2 border-white/20 hover:border-white text-white px-7 py-3 text-xs font-bold uppercase tracking-widest transition-all rounded-xl">
              Contact Us
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
